/**
 * Subject tracking for hooks: a hook can follow the person's head as the camera
 * (or the person) moves, keeping the same distance - and, optionally, scaling
 * with them when the camera pushes in.
 *
 * The track is derived server-side from the person matte (the same cut-out that
 * powers "behind the person"): per sample, the head position (normalized to the
 * SOURCE frame) and head width. Both preview and export evaluate it here.
 */
/** Interpolated head position at `timeMs`, mapped into a `width`×`height` frame. */
export function headAt(track, timeMs, width, height, fit = 'cover') {
    const n = Math.floor(track.pts.length / 3);
    if (n === 0 || !(track.fps > 0))
        return null;
    const f = Math.max(0, Math.min(n - 1, (timeMs / 1000) * track.fps));
    const i0 = Math.floor(f);
    const i1 = Math.min(n - 1, i0 + 1);
    const a = sampleAt(track, i0, n);
    const b = sampleAt(track, i1, n);
    if (!a && !b)
        return null;
    const A = a ?? b;
    const B = b ?? a;
    const k = f - i0;
    const xn = A[0] + (B[0] - A[0]) * k;
    const yn = A[1] + (B[1] - A[1]) * k;
    const sn = A[2] + (B[2] - A[2]) * k;
    // Map the source-normalized point into the output frame.
    const aspect = track.aspect > 0 ? track.aspect : width / height;
    const coverW = Math.max(width, height * aspect);
    const containW = Math.min(width, height * aspect);
    const dispW = fit === 'cover' ? coverW : containW;
    const dispH = dispW / aspect;
    const offX = (width - dispW) / 2;
    const offY = (height - dispH) / 2;
    return { x: offX + xn * dispW, y: offY + yn * dispH, size: sn * dispW };
}
/** Sample `i`, or the nearest sample that has a person (size > 0). */
function sampleAt(track, i, n) {
    for (let r = 0; r < 12; r++) {
        for (const j of r === 0 ? [i] : [i - r, i + r]) {
            if (j < 0 || j >= n)
                continue;
            const s = track.pts[j * 3 + 2];
            if (s > 0)
                return [track.pts[j * 3], track.pts[j * 3 + 1], s];
        }
    }
    return null;
}
/**
 * The block offset + scale that keeps a hook at the same relative spot to the
 * head. `center` is the hook block's static center. Returns identity when the
 * track has no person at either time.
 */
export function trackOffset(track, settings, hook, nowMs, center, frame) {
    const none = { dx: 0, dy: 0, scale: 1 };
    if (!track || !settings || settings.strength <= 0)
        return none;
    const h0 = headAt(track, hook.startMs, frame.width, frame.height, frame.fit);
    const h1 = headAt(track, nowMs, frame.width, frame.height, frame.fit);
    if (!h0 || !h1)
        return none;
    const strength = Math.min(1, Math.max(0, settings.strength));
    const rawK = settings.scale && h0.size > 0 ? h1.size / h0.size : 1;
    const k = 1 + (Math.min(2, Math.max(0.5, rawK)) - 1) * strength;
    // Keep the vector head→hook, scaled with the head.
    const tx = h1.x + (center.x - h0.x) * k;
    const ty = h1.y + (center.y - h0.y) * k;
    return { dx: (tx - center.x) * strength, dy: (ty - center.y) * strength, scale: k };
}
const norm = (s) => s.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');
/**
 * Time each explicit hook line to when it's SPOKEN: find the line's words in the
 * transcript near the hook window and return per-line reveal delays (ms after the
 * hook's start). Lines that can't be matched keep an even cadence.
 */
export function syncLinesToSpeech(hook, words) {
    const lines = hook.text.split('\n').map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0)
        return [];
    // Candidate words: a little before the hook through its end.
    const pool = words
        .filter((w) => w.endMs >= hook.startMs - 1500 && w.startMs <= hook.endMs + 500)
        .map((w) => ({ n: norm(w.text), t: w.startMs }))
        .filter((w) => w.n);
    const span = Math.max(1, hook.endMs - hook.startMs);
    const even = (i) => Math.round((span * 0.6 * i) / Math.max(1, lines.length));
    let cursor = 0;
    const out = [];
    lines.forEach((line, i) => {
        const first = norm(line.split(/\s+/)[0] ?? '');
        let hit = -1;
        if (first) {
            for (let j = cursor; j < pool.length; j++) {
                if (pool[j].n === first || (first.length > 3 && pool[j].n.startsWith(first))) {
                    hit = j;
                    break;
                }
            }
        }
        if (hit >= 0) {
            cursor = hit + 1;
            out.push(Math.max(0, pool[hit].t - hook.startMs));
        }
        else {
            out.push(Math.max(i > 0 ? out[i - 1] : 0, even(i)));
        }
    });
    // Monotonic + inside the window.
    for (let i = 1; i < out.length; i++)
        out[i] = Math.max(out[i], out[i - 1]);
    return out.map((d) => Math.min(d, Math.max(0, span - 300)));
}
//# sourceMappingURL=hook-track.js.map